// STOP-SHIP recovery: after the emergency update, import photos left behind
// by OLD app versions (older IndexedDB queue stores) so nothing on the phone
// is lost. Best-effort and defensive: anything unreadable is skipped, legacy
// records are marked migrated (never re-imported), and audio/walkthrough
// chunks are ignored (the walkthrough store handles those).
import { queueItem } from "@/lib/offlineQueue";

const KNOWN = new Set(["buildflow-offline", "buildflow-wt"]);

export async function recoverLegacyQueues() {
  if (typeof indexedDB === "undefined" || !indexedDB.databases) return 0;
  let dbs = [];
  try { dbs = await indexedDB.databases(); } catch { return 0; }
  let imported = 0;
  for (const { name } of dbs) {
    if (!name || KNOWN.has(name)) continue;
    let db = null;
    try {
      db = await new Promise((res, rej) => {
        const r = indexedDB.open(name); // no version → opens the existing one
        r.onsuccess = () => res(r.result);
        r.onerror = () => rej(r.error);
        r.onupgradeneeded = () => { r.transaction?.abort(); rej(new Error("unexpected upgrade")); };
      });
    } catch { continue; }
    try {
      for (const storeName of Array.from(db.objectStoreNames || [])) {
        let rows = [];
        try {
          rows = await new Promise((res, rej) => {
            const tx = db.transaction(storeName, "readonly");
            const req = tx.objectStore(storeName).getAll();
            req.onsuccess = () => res(req.result || []);
            req.onerror = () => rej(req.error);
          });
        } catch { continue; }
        for (const rec of rows) {
          try {
            if (!rec || typeof rec !== "object") continue;
            if (rec.migrated || rec.status === "uploaded") continue;
            if (rec.session_id && rec.seq != null) continue; // walkthrough audio chunk, not a photo
            const blob = rec.fileBlob || rec.blob || (rec.data instanceof Blob ? rec.data : null);
            if (!(blob instanceof Blob)) continue;
            await queueItem({
              kind: "photo", projectId: rec.projectId || rec.project_id || "",
              fileBlob: blob, filename: rec.filename || "recovered.jpg", type: blob.type || "image/jpeg",
              client_id: rec.client_id || undefined, // queueItem assigns one if missing
              device_capture_ts: rec.device_capture_ts || "", device_tz: rec.device_tz || "",
              lat: rec.lat ?? null, lng: rec.lng ?? null, accuracy_m: rec.accuracy_m ?? null,
              location_source: rec.location_source || "", source: rec.source || "upload",
              category: rec.category || "", note: rec.note || "", offline_created: true,
            });
            imported += 1;
            // Mark migrated in the legacy store so a later open never re-imports.
            try {
              const key = rec.qid ?? rec.id ?? rec.client_id;
              if (key != null) {
                await new Promise((res) => {
                  const tx = db.transaction(storeName, "readwrite");
                  const g = tx.objectStore(storeName).get(key);
                  g.onsuccess = () => { if (g.result) tx.objectStore(storeName).put({ ...g.result, migrated: true }); };
                  tx.oncomplete = () => res(true);
                  tx.onerror = () => res(false);
                });
              }
            } catch { /* best-effort */ }
          } catch { /* one bad record never stops the sweep */ }
        }
      }
    } finally {
      try { db.close(); } catch { /* ignore */ }
    }
  }
  return imported;
}
